import dayjs from "dayjs";
import { UserAvatar } from "common/elements";
import i18n from "features/shared/i18n";
import { FeedbackHistoryItem, FeedbackHistoryType, ContactType } from "features/player/types";

interface FeedbackChatMessageProps {
  item: FeedbackHistoryItem;
  childIcon?: string;
}

// 星評価を表示
const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex-row-el items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`text-[24px] ${star <= rating ? "text-yellow-500" : "text-gray-300"}`}
        >
          ★
        </span>
      ))}
    </div>
  );
};

// 種別バッジを表示
const TypeBadge = ({ type }: { type: FeedbackHistoryType }) => {
  const config = {
    [FeedbackHistoryType.RATING]: {
      icon: "star",
      label: i18n.t("MSG_PARENT_PAGE_FEEDBACK_TYPE_RATING"),
      color: "bg-yellow-100 text-yellow-700",
    },
    [FeedbackHistoryType.FEEDBACK]: {
      icon: "feedback",
      label: i18n.t("MSG_PARENT_PAGE_FEEDBACK_TYPE_FEEDBACK"),
      color: "bg-blue-50 text-blue-700",
    },
    [FeedbackHistoryType.CONTACT]: {
      icon: "mail",
      label: i18n.t("MSG_PARENT_PAGE_FEEDBACK_TYPE_CONTACT"),
      color: "bg-green-50 text-green-700",
    },
  };

  const { icon, label, color } = config[type];

  return (
    <div
      className={`flex-row-el items-center gap-1 px-2 py-0.5 rounded-[8px] text-[18px] ${color}`}
    >
      <span className="material-symbols-outlined text-[21px]">{icon}</span>
      {label}
    </div>
  );
};

// 問い合わせタイプを表示
const ContactTypeBadge = ({ contactType }: { contactType: ContactType }) => {
  const labels = {
    [ContactType.HOW_TO]: i18n.t("MSG_PARENT_PAGE_CONTACT_TYPE_HOW_TO"),
    [ContactType.BUG]: i18n.t("MSG_PARENT_PAGE_CONTACT_TYPE_BUG"),
    [ContactType.REQUEST]: i18n.t("MSG_PARENT_PAGE_CONTACT_TYPE_REQUEST"),
    [ContactType.OTHER]: i18n.t("MSG_PARENT_PAGE_CONTACT_TYPE_OTHER"),
  };

  return <span className="text-[18px] text-gray2-100">{labels[contactType]}</span>;
};

// complaint_type → i18nキーのマッピング
const COMPLAINT_TYPE_KEYS: Record<string, string> = {
  NONE: "MSG_REVIEW_FEEDBACK_TYPE_NONE",
  APP_HOW_TO_USE: "MSG_REVIEW_FEEDBACK_TYPE_APP_HOW_TO_USE",
  APP_HARD_TO_USE: "MSG_REVIEW_FEEDBACK_TYPE_APP_HARD_TO_USE",
  COURSE_TOO_DIFFICULT: "MSG_REVIEW_FEEDBACK_TYPE_COURSE_TOO_DIFFICULT",
  COURSE_TOO_EASY: "MSG_REVIEW_FEEDBACK_TYPE_COURSE_TOO_EASY",
  COURSE_TOO_MANY: "MSG_REVIEW_FEEDBACK_TYPE_COURSE_TOO_MANY",
  COURSE_TOO_FEW: "MSG_REVIEW_FEEDBACK_TYPE_COURSE_TOO_FEW",
  CREATOR_HARD_TO_CREATE: "MSG_REVIEW_FEEDBACK_TYPE_CREATOR_HARD_TO_CREATE",
  CREATOR_HARD_TO_USE: "MSG_REVIEW_FEEDBACK_TYPE_CREATOR_HARD_TO_USE",
};

// フィードバックタイプを表示（complaint_type）
const ComplaintTypeBadge = ({ complaintType }: { complaintType: string }) => {
  const types = complaintType.split(",").filter((t) => t.trim());
  const labels = types
    .map((type) => {
      const key = COMPLAINT_TYPE_KEYS[type.trim()];
      return key ? i18n.t(key).replace(/\n/g, "") : null;
    })
    .filter(Boolean);

  if (labels.length === 0) return null;

  return <span className="text-[18px] text-gray2-100">{labels.join(", ")}</span>;
};

// 日時フォーマット
const formatDate = (dateString: string) => {
  return dayjs(dateString).format("YYYY-MM-DD HH:mm");
};

export const FeedbackChatMessage = ({ item, childIcon }: FeedbackChatMessageProps) => {
  return (
    <div className="flex-col-el gap-3">
      {/* 子供の送信メッセージ（左側） */}
      <div className="flex justify-start gap-2">
        {/* 子供アイコン */}
        <div className="w-12 h-12 shrink-0">
          <UserAvatar icon={childIcon} size={48} />
        </div>

        {/* メッセージ本体 */}
        <div className="max-w-[70%] py-3 px-4 bg-white/80 text-textcolor-black rounded-[16px] rounded-bl-[4px] shadow-sm">
          {/* 種別バッジ */}
          <div className="flex-row-el items-center gap-2 mb-2 flex-wrap">
            <TypeBadge type={item.type} />
            {item.type === FeedbackHistoryType.CONTACT && item.contact_type && (
              <ContactTypeBadge contactType={item.contact_type} />
            )}
            {item.type === FeedbackHistoryType.FEEDBACK && item.complaint_type && (
              <ComplaintTypeBadge complaintType={item.complaint_type} />
            )}
          </div>

          {/* 内容 */}
          {item.type === FeedbackHistoryType.RATING && item.rating && (
            <div className="mb-2">
              <StarRating rating={item.rating} />
            </div>
          )}

          {item.detail && (
            <div className="text-[21px] leading-[1.6] text-textcolor-black mb-2">{item.detail}</div>
          )}

          {/* 日時 */}
          <div className="text-[16px] text-gray2-100">{formatDate(item.created_at)}</div>
        </div>
      </div>

      {/* 運営からの返信（右側） */}
      {item.reply && (
        <div className="flex justify-end gap-2">
          {/* 返信本体 */}
          <div className="max-w-[70%] py-3 px-4 bg-green-100 text-white rounded-[16px] rounded-tr-[4px] shadow-sm">
            {/* タイトル */}
            <div className="text-[21px] mb-1">{item.reply.title}</div>

            {/* 内容 */}
            <div className="text-[21px] leading-[1.6] mb-2">{item.reply.content}</div>

            {/* 日時 */}
            <div className="text-[16px] opacity-70">{formatDate(item.reply.published_at)}</div>
          </div>

          {/* 運営アイコン */}
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 overflow-hidden border-2 border-green-100">
            <span className="material-symbols-outlined text-[36px] text-green-100">
              support_agent
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
